import HBIcon from "@/components/HBIcon";
import Image from "@/components/Image";
import Masonry from "@/components/Masonry";
import { getImageSrc2x } from "@/utils/imageUrl";
import { useMemoizedFn } from "ahooks";

import { Button } from "antd";
import classNames from "classnames";
import Link from "next/link";
import { memo, useMemo } from "react";
import styles from "./index.module.less";

interface IProps {
  id?: string;
  title: string;
  subTitle: string;
  pins: IPin[];
  href: string;
  height?: number;
  loading?: boolean;
  /**相似素材的样式（粉色颜色主题） */
  isSimilar?: boolean;
  /**AI推荐素材的样式（蓝色颜色主题） */
  isAi?: boolean;
  width?: number;
}

function SimilarCard({
  id = "SimilarCard",
  title,
  subTitle,
  pins,
  href,
  height,
  loading,
  isSimilar,
  isAi,
  width = 240,
  ...rest
}: IProps & React.HTMLAttributes<HTMLAnchorElement>) {
  const renderListData: IPin[] = useMemo(() => {
    if (isAi) {
      return pins;
    }

    let data = [...(pins || [])];
    // 反转，避免与上面相似素材的顺序相同
    return data.reverse().splice(0, 15);
  }, [isAi, pins]);

  const getSize = useMemoizedFn((pin) => {
    if (isAi) {
      return {
        width: pin.preview.width,
        height: pin.preview.height,
      };
    } else {
      return {
        width: pin.file.width,
        height: pin.file.height,
      };
    }
  });

  const { columnSize, columnNum } = useMemo(() => {
    // 筛选出横图数量
    const horizontalList = renderListData.filter(
      (item) => getSize(item).width / getSize(item).height > 1.5
    );
    if (
      // 横图超过一半，值展示一列
      horizontalList.length >
      renderListData.length / 2
    ) {
      return {
        columnNum: 1,
        columnSize: width - 6,
      };
    } else {
      return {
        columnNum: 2,
        columnSize: width / 2 - 5,
      };
    }
  }, [getSize, renderListData, width]);

  const render = useMemoizedFn((pin) => {
    const imageSrc = isAi
      ? { src: pin.preview.url }
      : getImageSrc2x(pin.file, { width: columnSize });

    const height = Math.round(
      (getSize(pin).height / getSize(pin).width) * columnSize
    );

    if (isAi) {
      return (
        <a
          target="_blank"
          rel="nofollow"
          href={`https://www.gaoding.art/posts/${pin.id}`}
          className={styles.imgContent}
          onClick={(e) => {
            e.stopPropagation();
          }}
          data-gd-click="button_click"
          data-button-name="AI内容推荐内容"
        >
          <Image
            onLoad={(evt) => {
              const target = evt.target as HTMLElement;
              target?.classList.add("transparent-img-bg");
            }}
            {...imageSrc}
            width={columnSize}
            height={height}
            alt={pin.raw_text}
            showError={true}
          />
        </a>
      );
    }

    return (
      <div key={pin.pin_id || pin.id} className={styles.imgContent}>
        <Image
          onLoad={(evt) => {
            const target = evt.target as HTMLElement;
            target?.classList.add("transparent-img-bg");
          }}
          {...imageSrc}
          width={columnSize}
          height={height}
          alt={pin.raw_text}
          showError={true}
        />
      </div>
    );
  });

  return (
    <Link
      href={href}
      className={classNames(styles.card, {
        [styles.similar]: isSimilar,
        [styles.ai]: isAi,
      })}
      id={id}
      {...rest}
    >
      <div
        className={classNames(styles.content)}
        style={height ? { height: height - 76 } : null}
      >
        {!loading && (
          <Masonry
            bricks={renderListData}
            scrollElement={id}
            brickId={isAi ? "id" : "pin_id"}
            render={render}
            columnNum={columnNum}
            threshold={800}
            gutter={3}
            columnSize={columnSize}
          />
        )}
      </div>
      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          <div className={styles.title}># {title}</div>
          <div className={styles.subtitle}>
            {subTitle}{" "}
            {!isAi && <HBIcon className={styles.icon} type="ic_copyright" />}
          </div>
        </div>
        <div className={styles.footerRight}>
          {isAi ? (
            <Button type="primary" shape="round">
              更多
              <HBIcon type="ic_chevron_right" />
            </Button>
          ) : (
            <div className={styles.more}>
              更多
              <HBIcon type="ic_chevron_right" />
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
export default memo(SimilarCard);
